import { useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { builtInPages, type BuiltInPage } from '../../pages/registry';
import type { GroupedPageSection } from '../../types/config';
import './Sidebar.css';

interface SidebarProps {
  projectId: string;
}

export function Sidebar({ projectId }: SidebarProps) {
  const { userConfig } = useApp();
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Build navigation from userConfig
  const navigation = useMemo(() => {
    if (!userConfig?.groupedPages || !userConfig?.handlers) {
      return [];
    }

    return Object.entries(userConfig.groupedPages)
      .map(([key, section]: [string, GroupedPageSection]) => ({
        key,
        ...section,
        pages: section.pages.map(page => {
          const handler = userConfig.handlers[page.handler];
          return {
            ...page,
            handler: handler,
          };
        }).filter(p => p.handler),
      }))
      .sort((a, b) => a.position - b.position);
  }, [userConfig]);

  // Group built-in pages by their group property
  const builtInNavigation = useMemo(() => {
    const groups: Record<string, BuiltInPage[]> = {};
    
    builtInPages.forEach(page => {
      const groupName = page.group || 'Tools';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(page);
    });
    
    return Object.entries(groups).map(([groupName, pages]) => ({
      key: `builtin-${groupName.toLowerCase().replace(/\s+/g, '-')}`,
      groupName,
      pages,
    }));
  }, []);

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Check if any page in a group is active
  const isGroupActive = (section: typeof navigation[0]) => {
    return section.pages.some(page => 
      location.pathname.includes(page.handler?.path || '')
    );
  };

  // Auto-expand active groups
  useMemo(() => {
    navigation.forEach(section => {
      if (isGroupActive(section)) {
        setExpandedGroups(prev => new Set([...prev, section.key]));
      }
    });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!userConfig) {
    return (
      <nav className="sidebar">
        <div className="sidebar-empty">No navigation configured</div>
      </nav>
    );
  }

  // Check if a built-in page is active
  const isBuiltInPageActive = (page: BuiltInPage) => {
    return location.pathname.includes(page.path);
  };

  // Check if a built-in group has active page
  const isBuiltInGroupActive = (pages: BuiltInPage[]) => {
    return pages.some(page => isBuiltInPageActive(page));
  };

  return (
    <nav className="sidebar">
      <div className="sidebar-nav">
        {/* User config navigation */}
        {navigation.map(section => (
          <div 
            key={section.key} 
            className={`nav-group ${isGroupActive(section) ? 'active' : ''}`}
          >
            <button
              className="nav-group-header"
              onClick={() => toggleGroup(section.key)}
              aria-expanded={expandedGroups.has(section.key)}
            >
              <div className="nav-group-icon">
                <i className={section.icon.replace(' fa-2x', '')}></i>
              </div>
              <span className="nav-group-title">{section.key}</span>
              <i className={`fas fa-chevron-${expandedGroups.has(section.key) ? 'down' : 'right'} nav-group-arrow`}></i>
            </button>
            
            {expandedGroups.has(section.key) && (
              <div className="nav-group-items">
                {section.pages.map((page, index) => (
                  <NavLink
                    key={`${page.handler}-${index}`}
                    to={`/project/${projectId}${page.handler?.path}`}
                    className={({ isActive }) => 
                      `nav-item ${isActive ? 'active' : ''}`
                    }
                  >
                    <span className="nav-item-title">{page.page}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Built-in pages separator */}
        {builtInNavigation.length > 0 && navigation.length > 0 && (
          <div className="nav-separator" />
        )}

        {/* Built-in pages navigation */}
        {builtInNavigation.map(group => (
          <div 
            key={group.key} 
            className={`nav-group builtin ${isBuiltInGroupActive(group.pages) ? 'active' : ''}`}
          >
            <button
              className="nav-group-header"
              onClick={() => toggleGroup(group.key)}
              aria-expanded={expandedGroups.has(group.key)}
            >
              <div className="nav-group-icon">
                <i className="fas fa-cog"></i>
              </div>
              <span className="nav-group-title">{group.groupName}</span>
              <i className={`fas fa-chevron-${expandedGroups.has(group.key) ? 'down' : 'right'} nav-group-arrow`}></i>
            </button>
            
            {expandedGroups.has(group.key) && (
              <div className="nav-group-items">
                {group.pages.map(page => (
                  <NavLink
                    key={page.key}
                    to={`/project/${projectId}${page.path}`}
                    className={({ isActive }) => 
                      `nav-item ${isActive ? 'active' : ''}`
                    }
                  >
                    <i className={page.icon}></i>
                    <span className="nav-item-title">{page.title}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </nav>
  );
}

export default Sidebar;

